import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useProcessing } from '../../contexts/ProcessingContext';
import { useBrandKit } from '../../contexts/BrandKitContext';
import { assetsApi } from '../../lib/assetsApi';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import LockedBrandProcessor from './LockedBrandProcessor';

const { FiUpload, FiFile, FiX, FiDownload, FiEye, FiChevronRight, FiChevronLeft, FiLock } = FiIcons;

export default function DocumentProcessor() {
  const { uploadedFiles, addUploadedFile, setCurrentStep, setProcessing, progress, setProgress } = useProcessing();
  const { currentBrandKit } = useBrandKit();
  const [currentPage, setCurrentPage] = useState(0);
  const [documentPages, setDocumentPages] = useState([]);
  const [processingStatus, setProcessingStatus] = useState('idle'); // idle, uploading, complete
  const [activeDocument, setActiveDocument] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileUpload = async (files) => {
    setProcessing(true);
    setProcessingStatus('uploading');
    
    try {
      for (const file of files) {
        const uploadedAsset = await assetsApi.uploadAsset(file, 'document');
        addUploadedFile({
          id: uploadedAsset.id,
          name: file.name,
          size: file.size,
          type: file.type,
          url: uploadedAsset.url,
          dropbox_path: uploadedAsset.dropbox_path,
          originalFile: file
        });
        
        // Set the first uploaded file as active for processing
        if (!activeDocument) {
          setActiveDocument({
            id: uploadedAsset.id,
            name: file.name,
            size: file.size,
            type: file.type,
            url: uploadedAsset.url,
            dropbox_path: uploadedAsset.dropbox_path,
            originalFile: file
          });
        }
        
        // Create mock document pages for preview
        const mockPages = Array.from({ length: 5 }, (_, i) => ({
          id: i,
          pageNumber: i + 1,
          content: `Page ${i + 1} content mock`,
          originalContent: `Original page ${i + 1} content`
        }));
        
        setDocumentPages(mockPages);
      }
    } catch (error) {
      console.error('Failed to upload files:', error);
    } finally {
      setProcessing(false);
      setProcessingStatus('complete');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    handleFileUpload(files);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    handleFileUpload(files);
  };

  const handleProcessDocument = () => {
    if (activeDocument) {
      setCurrentStep('process');
    }
  };

  const handleDownloadDocument = () => {
    // Simulate document download
    console.log('Downloading branded document...');
  };

  const nextPage = () => {
    if (currentPage < documentPages.length - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  if (uploadedFiles.length === 0) {
    return (
      <div className="space-y-6">
        <motion.div 
          className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center hover:border-gray-400 transition-colors"
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          whileHover={{ scale: 1.02 }}
        >
          <SafeIcon icon={FiUpload} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Upload Documents</h3>
          <p className="text-gray-600 mb-4">Drag and drop your files here, or click to browse</p>
          <input 
            type="file" 
            multiple 
            onChange={handleFileSelect} 
            className="hidden" 
            id="file-upload" 
            accept=".pdf,.doc,.docx,.ppt,.pptx,.jpg,.jpeg,.png" 
            ref={fileInputRef}
          />
          <label 
            htmlFor="file-upload" 
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition-colors"
          >
            <SafeIcon icon={FiUpload} className="w-4 h-4 mr-2" />
            Browse Files
          </label>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Document Processing</h2>
            <div className="flex items-center mt-1">
              <SafeIcon icon={FiLock} className="w-4 h-4 text-gray-500 mr-1" />
              <p className="text-sm text-gray-600">Using locked brand assets from {currentBrandKit?.name || 'your brand kit'}</p>
            </div>
          </div>
          <div className="flex space-x-2">
            <button 
              onClick={() => {
                setDocumentPages([]);
                setCurrentStep('upload');
              }}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Upload New
            </button>
            <button 
              onClick={handleProcessDocument}
              disabled={!activeDocument}
              className={`px-4 py-2 rounded-lg transition-colors ${
                !activeDocument 
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
            >
              Process Document
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Document Preview */}
          <div className="lg:col-span-2">
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-medium text-gray-900">
                  {activeDocument?.name || 'Document Preview'}
                </h3>
                <div className="flex space-x-2">
                  <button 
                    onClick={prevPage}
                    disabled={currentPage === 0}
                    className="p-2 rounded-lg bg-white border border-gray-300 disabled:opacity-50"
                  >
                    <SafeIcon icon={FiChevronLeft} className="w-4 h-4" />
                  </button>
                  <span className="flex items-center px-3 py-2 bg-white border border-gray-300 rounded-lg">
                    Page {currentPage + 1} of {documentPages.length}
                  </span>
                  <button 
                    onClick={nextPage}
                    disabled={currentPage === documentPages.length - 1}
                    className="p-2 rounded-lg bg-white border border-gray-300 disabled:opacity-50"
                  >
                    <SafeIcon icon={FiChevronRight} className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              {documentPages.length > 0 ? (
                <div className="relative w-full h-96 bg-white border border-gray-300 rounded-lg overflow-hidden">
                  <div className="p-8 h-full">
                    <div className="h-full overflow-y-auto">
                      <h1 className="text-2xl font-bold mb-4" style={{ color: '#03245E' }}>
                        Document Title
                      </h1>
                      <p className="mb-4">
                        This is a preview of your document with the selected branding applied. 
                        The font settings, colors, and positioning of logos and watermarks 
                        are strictly adhering to your locked brand assets.
                      </p>
                      <p className="mb-4">
                        Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod 
                        tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim 
                        veniam, quis nostrud exercitation ullamco laboris.
                      </p>
                      <ul className="list-disc pl-6 mb-4">
                        <li>First item in a list</li>
                        <li>Second item with branding applied</li>
                        <li>Third item showing font styling</li>
                      </ul>
                    </div>
                  </div>
                  
                  {/* Logo overlay */}
                  <div className="absolute top-4 right-4 w-24 h-24 bg-gray-200 border-2 border-dashed border-gray-400 rounded flex items-center justify-center opacity-80">
                    <SafeIcon icon={FiImage} className="w-1/2 h-1/2 text-gray-400" />
                  </div>
                  
                  {/* Watermark overlay */}
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-gray-200 border-2 border-dashed border-gray-400 rounded flex items-center justify-center opacity-30">
                    <SafeIcon icon={FiDroplet} className="w-1/2 h-1/2 text-gray-400" />
                  </div>
                </div>
              ) : (
                <div className="h-96 flex items-center justify-center bg-white border-2 border-dashed border-gray-300 rounded-lg">
                  <p className="text-gray-500">Document preview will appear here</p>
                </div>
              )}
            </div>
          </div>

          {/* Document List */}
          <div className="space-y-4">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h3 className="font-semibold text-gray-900 mb-4">Uploaded Documents</h3>
              <div className="space-y-3">
                {uploadedFiles.map((file, index) => (
                  <div 
                    key={index}
                    className={`flex items-center p-3 rounded-lg cursor-pointer transition-colors ${
                      activeDocument?.id === file.id 
                        ? 'bg-blue-50 border border-blue-200' 
                        : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                    onClick={() => setActiveDocument(file)}
                  >
                    <SafeIcon icon={FiFile} className="w-5 h-5 text-gray-500 mr-3" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{file.name}</p>
                      <p className="text-sm text-gray-500">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    {activeDocument?.id === file.id && (
                      <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center">
                        <SafeIcon icon={FiCheck} className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <SafeIcon icon={FiLock} className="w-4 h-4 mr-2 text-gray-500" />
                Locked Brand Assets
              </h3>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Primary Color</p>
                  <div className="flex items-center">
                    <div className="w-6 h-6 rounded border border-gray-300 mr-2" style={{ backgroundColor: '#03245E' }}></div>
                    <span className="text-sm font-medium">#03245E</span>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Accent Color</p>
                  <div className="flex items-center">
                    <div className="w-6 h-6 rounded border border-gray-300 mr-2" style={{ backgroundColor: '#E2C164' }}></div>
                    <span className="text-sm font-medium">#E2C164</span>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Body Font</p>
                  <p className="text-sm font-medium">Helvetica World</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Title Font</p>
                  <p className="text-sm font-medium">Anton</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}